import { CircularProgress } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectAdminTimeOffRequestData,
  updateSortModel as updateAdminSortModel,
} from "../slices/adminTimeOfRequest/adminTimeOfRequestSlice";
import {
  selectTimeOffRequest,
  updateSortModel as updateEmployeeSortModel,
} from "../slices/timeOffRequest/timeOffRequestSlice";
import { generateExportCsvButtonText } from "../util/commonUtils";
import { ACTIONS, LAST_UPDATED, SHIFT, STATUS, TIME_OFF_PAGE_TYPE, TIME_OFF_TYPE } from "../util/constants";
import { getFormattedTimeStamp } from "../util/dateTimeFormatter";
import { format_ShortDOW_Date, getShiftTime } from "../util/dateTimeFormatter.js";
import {
  getSortedTimeOffRequestByStatus,
  getSortedTimeOffRequestByTimeOffType,
  getSortedTimeOffRequestsByLastUpdated,
  getSortedTimeOffRequestsByShift,
  getTimeOffEmployeeLastNameCustomComparator,
} from "../util/sortTimeOffRequests";
import {
  getTORApprovedLeaveTime,
  getTOREmployeeId,
  getTOREmployeeName,
  getTORPreferredLeaveTime,
  getTORShift,
  getTORstation,
} from "../util/timeOffExportMapper";
import AdminTimeOffRequestToolbar from "./AdminTimeOffRequestToolbar";
import CustomErrorTableOverlay from "./CustomErrorTableOverlay";
import CustomLoadingTableOverlay from "./CustomLoadingTableOverlay";
import CustomNoRowsTableOverlay from "./CustomNoRowsTableOverlay";
import NoTimeOffRequestImage from "./NoTimeOffRequestsImage";
import StatusCell from "./StatusCell";
import TimeOffRequestAdminAction from "./TimeOffRequestAdminAction";
import TimeOffRequestEmployeeAction from "./TimeOffRequestEmployeeAction";
import TimeOffRequestEmployeeCell from "./TimeOffRequestEmployeeCell.jsx";
import TimeOffRequestTableMobile from "./TimeOffRequestTableMobile";
import TimeOffRequestType from "./TimeOffRequestType";

const EmployeeTimeOffRequestTable = (props) => {
  const [selectionModel, setSelectionModel] = useState([]);
  const adminTimeOffRequestData = useSelector(selectAdminTimeOffRequestData);
  const employeeTimeOffRequestData = useSelector(selectTimeOffRequest);
  const dispatch = useDispatch();

  const renderTimeOffTypeCell = (timeOffRequest) => {
    return (
      <TimeOffRequestType
        timeOffRequest={timeOffRequest}
        isAdminPage={props.isAdminPage}
        isDesktopView={props.isDesktopView}
      />
    );
  };

  const renderStatusTypeCell = (timeOffRequest) => {
    return <StatusCell status={timeOffRequest.visibleStatus} isAdminPage={props.isAdminPage} />;
  };

  const renderShiftCell = (timeOffRequest) => {
    return (
      <div className="shift-cell">
        {format_ShortDOW_Date(timeOffRequest.shiftStart) +
          " " +
          getShiftTime(timeOffRequest.shiftStart) +
          "-" +
          getShiftTime(timeOffRequest.shiftEnd)}
      </div>
    );
  };

  const renderPreferredLeaveTimeCell = (timeOffRequest) => {
    return (
      <div className="preferredLeave-cell">
        {timeOffRequest.approvedWOPStart && timeOffRequest.approvedWOPEnd
          ? getTORApprovedLeaveTime(timeOffRequest)
          : getTORPreferredLeaveTime(timeOffRequest)}
      </div>
    );
  };

  const renderActionCell = (timeOffRequest) => {
    return props.isAdminPage ? (
      <TimeOffRequestAdminAction timeOffRequest={timeOffRequest} />
    ) : (
      <TimeOffRequestEmployeeAction timeOffRequest={timeOffRequest} />
    );
  };

  const renderEmployee = (timeOffRequests) => {
    let empData = {
      employeeLastName: timeOffRequests.empLastName,
      employeeFirstName: timeOffRequests.empFirstName,
      employeeId: timeOffRequests.empId,
    };
    return <TimeOffRequestEmployeeCell empData={empData} />;
  };

  const renderTimeOffTable = () => {
    const columns = [
      {
        field: "lastUpdatedDate",
        headerName: LAST_UPDATED,
        type: "dateTime",
        flex: 0.2,
        valueFormatter: (params) => {
          return getFormattedTimeStamp(params.value);
        },
        sortComparator: getSortedTimeOffRequestsByLastUpdated,
      },
      {
        field: "timeOffRequestType",
        headerName: TIME_OFF_TYPE,
        type: "string",
        flex: 0.15,
        renderCell: (params) => {
          return renderTimeOffTypeCell(params.row);
        },
        sortComparator: getSortedTimeOffRequestByTimeOffType,
      },
      {
        field: "visibleStatus",
        headerName: STATUS,
        type: "string",
        flex: 0.15,
        renderCell: (params) => {
          return renderStatusTypeCell(params.row);
        },
        sortComparator: getSortedTimeOffRequestByStatus,
      },
      { field: "workgroup", headerName: "Workgroup", type: "string", flex: 0.16, hide: !props.isAdminPage },
      {
        field: "employee",
        valueGetter: (params) => {
          return params.value;
        },
        renderCell: (params) => {
          return renderEmployee(params.row);
        },
        headerName: "Employee",
        type: "string",
        flex: 0.4,
        hide: !props.isAdminPage,
        sortComparator: getTimeOffEmployeeLastNameCustomComparator,
      },
      {
        field: "shift",
        headerName: SHIFT,
        type: "string",
        flex: 0.4,
        valueGetter: (params) => {
          return params.row;
        },
        renderCell: (params) => {
          return renderShiftCell(params.row);
        },
        valueFormatter: ({ value }) => {
          return getTORShift(value);
        },
        sortComparator: getSortedTimeOffRequestsByShift,
      },
      {
        field: "preferredWOPLeaveTime",
        headerName: "Leave Time",
        flex: 0.4,
        valueGetter: (params) => {
          return params.row;
        },
        renderCell: (params) => {
          return renderPreferredLeaveTimeCell(params.row);
        },
        type: "string",
        hide: props.isAdminPage,
      },
      {
        field: "actions",
        headerName: ACTIONS,
        //headerAlign: 'center',
        type: "string",
        flex: 0.15,
        sortable: false,
        renderCell: (params) => {
          return renderActionCell(params.row);
        },
      },
      {
        field: "station",
        valueGetter: (params) => {
          return params.row;
        },
        valueFormatter: ({ value }) => {
          return getTORstation(value);
        },
        type: "string",
        headerName: "Station",
        hide: true,
      },
      {
        field: "referenceNumber",
        headerName: "Reference #",
        type: "string",
        hide: true,
      },
      {
        field: "employeeName",
        valueGetter: (params) => {
          return params.row;
        },
        valueFormatter: ({ value }) => {
          return getTOREmployeeName(value);
        },
        headerName: "EE Name",
        type: "string",
        hide: true,
      },
      {
        field: "employeeId",
        headerName: "EE ID",
        valueGetter: (params) => {
          return params.row;
        },
        valueFormatter: ({ value }) => {
          return getTOREmployeeId(value);
        },
        type: "string",
        hide: true,
      },
    ];

    if (props.isDesktopView) {
      return (
        <div style={{ width: "100%", height: "inherit" }}>
          <DataGridPro
            apiRef={props.torTableDesktopRef}
            rows={props.timeOffRequests}
            columns={columns}
            sortModel={props.isAdminPage ? adminTimeOffRequestData.sortModel : employeeTimeOffRequestData.sortModel}
            onSortModelChange={(event) => {
              dispatch(props.isAdminPage ? updateAdminSortModel(event) : updateEmployeeSortModel(event));
            }}
            sortingOrder={["asc", "desc"]}
            disableColumnMenu={true}
            checkboxSelection={props.isAdminPage ? true : false}
            selectionModel={selectionModel}
            onSelectionModelChange={(newSelectionModel) => {
              setSelectionModel(newSelectionModel);
            }}
            onCellClick={(params, event) => {
              event.defaultMuiPrevented = true;
              setSelectionModel([params.id]);
            }}
            onRowClick={(params, event) => {
              event.defaultMuiPrevented = true;
            }}
            hideFooterPagination={true}
            rowHeight={props.isAdminPage ? 90 : 60}
            components={{
              LoadingOverlay: CustomLoadingTableOverlay,
              NoRowsOverlay: CustomNoRowsTableOverlay,
              ErrorOverlay: CustomErrorTableOverlay,
              Toolbar: props.isAdminPage ? AdminTimeOffRequestToolbar : null,
            }}
            componentsProps={{
              noRowsOverlay: { pageType: TIME_OFF_PAGE_TYPE },
              errorOverlay: {
                errorMessage: props.errorObject.message,
                errorCode: props.errorObject.code,
              },
              toolbar: { station: props.station },
            }}
            loading={props.isLoading}
            error={props.isError ? true : undefined}
            localeText={{
              toolbarExport: "Export",
              toolbarExportCSV: generateExportCsvButtonText(selectionModel, props.timeOffRequests),
            }}
          />
        </div>
      );
    } else {
      return (
        <div ref={props.torTableMobileRef} className="enable-scroll">
          {props?.isLoading ? (
            <CircularProgress />
          ) : props?.timeOffRequests?.length === 0 ? (
            <NoTimeOffRequestImage />
          ) : (
            <TimeOffRequestTableMobile
              handleOnEditTimeOffRequest={() => {}}
              timeOffRequestsList={props?.timeOffRequests}
              isEditable={false}
              showTransactions={true}
              isDesktopView={props.isDesktopView}
            />
          )}
        </div>
      );
    }
  };

  return <div className="stars-table">{renderTimeOffTable()}</div>;
};

EmployeeTimeOffRequestTable.propTypes = {
  timeOffRequests: PropTypes.array.isRequired,
  isAdminPage: PropTypes.bool.isRequired,
  isFirstLoad: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
  errorObject: PropTypes.object.isRequired,
  isDesktopView: PropTypes.bool.isRequired,
  station: PropTypes.string,
  torTableDesktopRef: PropTypes.object.isRequired,
  torTableMobileRef: PropTypes.object,
};

export default EmployeeTimeOffRequestTable;
